import './media.scss';

import { timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators'
import { defineComponent } from 'vue'
import { faImage } from '@fortawesome/free-regular-svg-icons'
import { faTabletAlt } from '@fortawesome/free-solid-svg-icons'
import { faCircleNotch, faTimes } from '@fortawesome/free-solid-svg-icons'

import { MediaType } from '@skinsoft/types';
import { itemSelectors } from '@skinsoft/selectors'
import { extractAndRemoveExifOrientationFromMedia, getImageOrientation, rotateImage } from '@skinsoft/media-transfer-helper'
import { isImageOrientationAutoManaged } from '@skinsoft/utils'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { FREE_MEDIA_CACHE_TOPIC, MEDIA_END_LOADING_TOPIC, MEDIA_ID_CHANGED_FROM_CACHE } from '@skinsoft/bus/src/busTopics'

export const MEDIA_DATA_CHANGED = 'media-data-changed'

export interface MediaIdChangedFromCacheEvent {
    oldMediaId: string,
    newMediaId: string
}

export const Media = defineComponent({
    mixins: [reactiveVueMixin],
    props: ['item', 'mediaId', 'onlyInCache', 'emitDataChange', 'defaultMedia', 'emitMediaLoaded'],
    template: require('./media.html'),
    data() {
        return {
            media: null as any as string,
            loadingIcon: faCircleNotch,
            errorIcon: faTimes,
            error: false,
            pending: false,
            // Icons
            mediaIcon: faImage,
            localIcon: faTabletAlt,
            orientationClass: "",
            fileSource: "",
        }
    },
    mounted() {
        this.media = this.defaultMedia
        this.fetchMedia()
        this.subscribeToBus(FREE_MEDIA_CACHE_TOPIC, () => { this.media = null as any })
    },
    methods: {
        isMediaLocal() {
            return this.media && this.fileSource === "cache";
        },
        fetchMedia() {
            const mediaId = this.item !== undefined ? itemSelectors.media(this.item) : this.mediaId
            if (mediaId) {
                // If mediaId is set to loading, display a forever spinning media
                if (mediaId === "loading") {
                    this.pending = true
                } else {
                    this.pending = true

                    if(window.navigator.onLine) {
                        const timer$ = timer(10000)
                        this.subscribeTo(this.$imageTransferHelper.downloadImage(mediaId, 'vignette250', this.onlyInCache).pipe(
                                takeUntil(timer$),
                                finalize(() => {
                                    if(this.emitMediaLoaded) {
                                        this.$emit(MEDIA_END_LOADING_TOPIC)
                                    }
                                })
                            ),
                            media => {
                                if(media) {
                                    if(media.dataUrl) {
                                        this.media = media.dataUrl;
                                        this.adaptMediaForDisplay(this.media)
                                        if(this.emitDataChange) {
                                            this.$emit(MEDIA_DATA_CHANGED, this.media)
                                        }
                                    }
                                    this.fileSource = media.source;
                                    this.pending = false;
                                    if (media.newMediaId) {
                                        // Notifying parent that the media id has changed, from a backend operation
                                        this.$emit(MEDIA_ID_CHANGED_FROM_CACHE, { oldMediaId: mediaId, newMediaId: media.newMediaId })
                                        this.unsubscribeFrom('media')
                                    }else if(mediaId.startsWith('local:') && media.dataUrl === undefined) {
                                        //Try to search image from filename
                                        const mediaType = this.$mediaTransferHelper.getMediaType(mediaId)
                                        switch(mediaType) {
                                            case MediaType.IMAGE:
                                                this.$imageTransferHelper.replaceImageCacheKeyWithIdFromFileName(mediaId.substring(mediaId.indexOf(':') + 1))
                                                break;
                                        }
                                    }
                                } else {
                                    this.pending = false
                                }
                            },
                            'media',
                        )
                    } else {
                        this.fetchMediaOffline(mediaId)
                    }
                }
            }
        },
        fetchMediaOffline(mediaId) {
            this.$imageTransferHelper.downloadImageOffline(mediaId).subscribe({
                next: media => {
                    this.media = media.dataUrl
                    if(this.media) {
                        this.adaptMediaForDisplay(this.media)
                    }
                    if(this.emitDataChange) {
                        this.$emit(MEDIA_DATA_CHANGED, this.media)
                    }
                    this.fileSource = media.source
                    this.pending = false
                },
                complete: () => {
                    if(this.emitMediaLoaded) {
                        this.$emit(MEDIA_END_LOADING_TOPIC)
                    }
                }
            })
        },
        async getRotateClass() {
            (this.$refs.img as any).onload = async () => {
                let orientation = 1;
                orientation = isImageOrientationAutoManaged() ? 1 : await getImageOrientation(this.$refs.img)
                switch (orientation) {
                    case 3:
                        this.orientationClass = "rotate180"
                        break;
                    case 6:
                        this.orientationClass = "rotate90"
                        break;
                    case 8:
                        this.orientationClass = "rotateM90"
                        break;
                    default:
                        this.orientationClass = ""
                        break;
                }
            }
        },
        adaptMediaForDisplay(base64Media: string) {
            const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(base64Media)
            if(orientation != -1) {
                const image = new Image()
                image.src = mediaWithoutOrientation as any

                image.onload = () => {
                    try {
                        this.media = rotateImage(image, orientation)
                    } catch(err) {
                        // logger media failed to rotate image
                        this.media = base64Media
                    }
                }
            } else {
                this.media = base64Media
            }
        },
    },
    watch: {
        item(item) {
            this.unsubscribeFrom('media');
            this.fetchMedia();
        },
        mediaId(media) {
            this.fetchMedia();
        },
    },
})
